import React, { useState } from "react";
import { Button, Collapse, Flex } from "antd";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { StarFilled } from "@ant-design/icons";
import { ItineraryActivity, ItineraryHotel, ItineraryPlan } from "../../../interfaces/itinerary.interface";
import { ITINERARY_TIMELINE_COPY, ItineraryLanguage } from "../itineraryTimelineCopy";
import Icon from "@mdi/react";
import { mdiChevronUp, mdiChevronDown } from "@mdi/js";
import { DownOutlined, UpOutlined } from '@ant-design/icons';

const PRIMARY_COLOR = "#DC1A22";

// MOB / MOB (Light Snack) / Afternoon Tea / MOB (Light Snack) / MOB (Light Snack)
const MEAL_CN: Record<string, string> = {
  Breakfast: "早餐",
  Lunch: "午餐",
  Dinner: "晚餐",
  MOB: "机上用餐",
  MGD: "米其林指南餐",
  "MOB (Light Snack)": "机上用餐（小食）",
  "Afternoon Tea": "下午茶",
  "Supper": "夜宵",
  "-": "-",
};

const PREVIEW_BANNER_TEXT: Record<string, string> = {
  ready_to_review: "Preview — this itinerary is ready for review and not yet published to customers.",
  ready_to_approve: "Preview — this itinerary is ready to approve and not yet published to customers.",
};
const DEFAULT_PREVIEW_BANNER_TEXT = "Preview — this itinerary is pending final review and is not yet published to customers.";

const Stars: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? (
    <span className="hotel-card__stars">
      {Array.from({ length: count }).map((_, i) => (
        <StarFilled key={i} />
      ))}
    </span>
  ) : null;

// Horizontal, scrollable image row for an activity/hotel.
const ImageStrip: React.FC<{ images: string[]; alt: string; onImageClick: (index: number) => void }> = ({
  images,
  alt,
  onImageClick,
}) => (
  <div className="image-strip">
    {images.map((src, ii) => (
      <img key={ii} src={src} alt={alt} onClick={() => onImageClick(ii)} />
    ))}
  </div>
);

export interface ItineraryPlanDetail {
  type: "activity" | "hotel";
  heading: string;
  title: string;
  tagline?: string;
  description?: string;
  stars?: number;
  images: string[];
  initialSlide?: number;
}

interface ItineraryPlanV3Props {
  plans: ItineraryPlan[];
  isMobile: boolean;
  generalContent?: string;
  generalContentTwo?: string;
  additionalTour?: string;
  optionalTour?: string;
  specialDisclaimer?: string;
  specialDisclaimerTwo?: string;
  isPreview?: boolean;
  previewStatus?: "ready_to_review" | "ready_to_approve" | null;
  onOpenDetail: (detail: ItineraryPlanDetail) => void;
}

const ItineraryPlanV3: React.FC<ItineraryPlanV3Props> = ({
  plans,
  isMobile,
  generalContent,
  generalContentTwo,
  additionalTour,
  optionalTour,
  specialDisclaimer,
  specialDisclaimerTwo,
  isPreview,
  previewStatus,
  onOpenDetail,
}) => {
  const [lang, setLang] = useState<ItineraryLanguage>("en");
  const [activeKeys, setActiveKeys] = useState<string[]>(() => (plans[0] ? [String(plans[0].day)] : []));

  const allKeys = plans.map((p) => String(p.day));
  const allExpanded = plans.length > 0 && activeKeys.length === plans.length;

  const toggleShowAll = () => setActiveKeys(allExpanded ? [] : allKeys);

  // Opening a day closes any other open day; closing one just removes that
  // day (matches desktop's accordion behavior).
  const handleAccordionChange = (keys: string | string[]) => {
    const newKeys = Array.isArray(keys) ? keys : [keys];
    const toggledKey =
      newKeys.find((k) => !activeKeys.includes(k)) ??
      activeKeys.find((k) => !newKeys.includes(k));
    if (toggledKey === undefined) {
      setActiveKeys(newKeys);
      return;
    }
    const isOpening = !activeKeys.includes(toggledKey);
    setActiveKeys(isOpening ? [toggledKey] : activeKeys.filter((k) => k !== toggledKey));
  };

  const t = (en?: string, cn?: string) => (lang === "cn" && cn ? cn : en ?? "");
  const copy = ITINERARY_TIMELINE_COPY[lang];

  const getImages = (item: ItineraryActivity | ItineraryHotel) => {
    const imagesTwo = item.images_two;
    return lang === "cn" && imagesTwo?.length ? imagesTwo : (item.images ?? []);
  };

  const hasActivityModalContent = (activity: ItineraryActivity) =>
    (getImages(activity).length > 0) || !!activity.description || !!activity.description_two;

  const hasHotelModalContent = (hotel: ItineraryHotel) =>
    (getImages(hotel).length > 0) || !!hotel.description || !!hotel.description_two;

  const openActivityDetail = (activity: ItineraryActivity, imageIndex = 0) =>
    onOpenDetail({
      type: "activity",
      heading: copy.activityDetails,
      title: t(activity.title, activity.title_two),
      tagline: t(activity.tagline, activity.tagline_two),
      description: t(activity.description, activity.description_two),
      images: getImages(activity),
      initialSlide: imageIndex,
    });

  const openHotelDetail = (hotel: ItineraryHotel) =>
    onOpenDetail({
      type: "hotel",
      heading: copy.hotelDetails,
      title: t(hotel.name, hotel.name_two),
      stars: parseInt(hotel.category ?? "0", 10) || 0,
      description: t(hotel.description, hotel.description_two),
      images: getImages(hotel),
    });

  const renderMealBadge = (displayMeals: string) => {
    const label = lang === "cn"
      ? displayMeals.split(" / ").map((m) => MEAL_CN[m] ?? m).join(" / ")
      : displayMeals;
    return <span className="day-badge active meal-badge">
      { isMobile ? '' : 
        <img className="itinerary_icon" src="/iceholidays-assets/images/v3/itinerary-icon.svg" alt="" aria-hidden="true" />
      }
      {label}
      </span>;
  };

  const hasMOB = plans.some((p) => p.display_meals?.includes("MOB"));
  const hasMGD = plans.some((p) => p.display_meals?.includes("MGD"));

  return (
    <>
      {isPreview && (
        <div className="itinerary-tab__preview-banner">
          {(previewStatus && PREVIEW_BANNER_TEXT[previewStatus]) || DEFAULT_PREVIEW_BANNER_TEXT}
        </div>
      )}

      {/* Section header */}
      <Flex justify="space-between" align="center" className="itinerary-tab__header">
        <Flex align="center" gap={10}>
          <span className="section-sublabel">Day Plans</span>
          <div className="itinerary-tab__lang-toggle">
            <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>{ITINERARY_TIMELINE_COPY.en.language}</button>
            <span className="divider">|</span>
            <button className={lang === "cn" ? "active" : ""} onClick={() => setLang("cn")}>{ITINERARY_TIMELINE_COPY.cn.language}</button>
          </div>
        </Flex>
        <Button type="link" onClick={toggleShowAll} style={{ color: PRIMARY_COLOR, padding: 0 }}>
          {allExpanded ? "Collapse All" : "Show All"}
        </Button>
      </Flex>

      {/* Days — accordion */}
      <Collapse
        activeKey={activeKeys}
        onChange={handleAccordionChange}
        expandIconPosition="end"
        expandIcon={({ isActive }) =>
          isMobile ? isActive ? <DownOutlined className="stepper-step__chevron" /> : <UpOutlined className="stepper-step__chevron" /> : <Icon className="itinerary-card_right-icon" path={ isActive ? mdiChevronDown : mdiChevronUp} size="24px" color="#1E1E1E" />
        }
        className="itinerary-plan-accordion"
        items={plans.map((plan: ItineraryPlan) => {
          const showMeals = plan.display_meals && plan.display_meals !== "N/A";

          return {
            key: String(plan.day),
            label: (
              <div className="itinerary-plan-day-header">
                <div className="itinerary-plan-day-header_row">
                  <span className="day-badge active">DAY {plan.day}</span>
                  {!isMobile && <span className="day-title active">{t(plan.title, plan.title_two)}</span>}
                  {showMeals && renderMealBadge(plan.display_meals!)}
                </div>
                {isMobile && <span className="day-title active">{t(plan.title, plan.title_two)}</span>}
              </div>
            ),
            children: (
              <div className="itinerary-tab__day-body">
                {t(plan.description, plan.description_two) && (
                  <div className="itinerary-modal-body__desc itinerary-tab__description">
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {t(plan.description, plan.description_two)}
                    </Markdown>
                  </div>
                )}

                {/* Activities */}
                {plan.activities?.length > 0 && (
                  <ul className="activity-list">
                    {plan.activities.map((activity: ItineraryActivity, aidx: number) => {
                      const actTitle = t(activity.title, activity.title_two);
                      const actTagline = t(activity.tagline, activity.tagline_two);
                      const actSubtitle = t(activity.subtitle, activity.subtitle_two);
                      const subtitleLines = actSubtitle
                        .split(/\r?\n/)
                        .map((line) => line.trim())
                        .filter(Boolean);
                      const actImages = getImages(activity);
                      const hasDetail = hasActivityModalContent(activity);
                      const isOptional = activity.category === "recommended_optional";

                      return (
                        <li key={aidx} className="activity-list__item">
                          <div className="activity-list__row">
                            <span className="activity-list__bullet" />
                            <span
                              className={`activity-title${hasDetail ? " is-clickable" : " is-plain"}`}
                              onClick={hasDetail ? () => openActivityDetail(activity) : undefined}
                            >
                              {actTitle}
                            </span>
                            {actTagline ? (
                              <span className="activity-tagline activity-tagline--inline">{actTagline}</span>
                            ) : null}
                            {isOptional && (
                              <span className="activity-category-tag activity-category-tag--recommended_optional">
                                Optional
                              </span>
                            )}
                          </div>
                          {subtitleLines.map((line, sidx) => (
                            <div key={sidx} className="activity-subtitle-row">
                              <span className="activity-list__bullet activity-list__bullet--sub" />
                              <span className="activity-subtitle">{line}</span>
                            </div>
                          ))}
                          {actImages.length > 0 && (
                            <ImageStrip
                              images={actImages.slice(0, 3)}
                              alt={actTitle}
                              onImageClick={(ii) => openActivityDetail(activity, ii)}
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Hotels */}
                {plan.hotels?.length > 0 && (
                  <div className="hotel-section">
                    <span className="stay-at-label">{copy.stayAt}</span>
                    {(() => {
                      const primaryHotel = plan.hotels[0];
                      const primaryName = t(primaryHotel.name, primaryHotel.name_two);
                      const primaryStars = parseInt(primaryHotel.category ?? "0", 10) || 0;
                      const primaryImages = getImages(primaryHotel);
                      const hasDetail = hasHotelModalContent(primaryHotel);
                      return (
                        <div
                          className={`hotel-card${hasDetail ? " is-clickable" : ""}`}
                          onClick={hasDetail ? () => openHotelDetail(primaryHotel) : undefined}
                        >
                          {primaryImages.length > 0 && (
                            <img
                              src={primaryImages[0]}
                              alt={primaryName}
                              className="hotel-card__thumb"
                            />
                          )}
                          <div className="hotel-card__info">
                            <span className="hotel-card__name">{primaryName}</span>
                            <Stars count={primaryStars} />
                          </div>
                          {hasDetail && <span className="hotel-card__arrow">&#8250;</span>}
                        </div>
                      );
                    })()}
                    {plan.hotels.slice(1).map((hotel: ItineraryHotel, hi: number) => {
                      const hasDetail = hasHotelModalContent(hotel);
                      return (
                        <span key={hi} className="hotel-alt">
                          {copy.or}{" "}
                          <span
                            className={hasDetail ? "hotel-alt__name" : "hotel-alt__name hotel-alt__name--plain"}
                            onClick={hasDetail ? () => openHotelDetail(hotel) : undefined}
                          >
                            {t(hotel.name, hotel.name_two)}
                          </span>
                        </span>
                      );
                    })}
                    <span className="hotel-alt hotel-alt--plain">{copy.similarClass}</span>
                  </div>
                )}
              </div>
            ),
          };
        })}
      />

      {t(generalContent, generalContentTwo) && (
        <div className="itinerary-modal-body__desc itinerary-tab__content-box itinerary-tab__general-content">
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {t(generalContent, generalContentTwo)}
          </Markdown>
        </div>
      )}

      {additionalTour && (
        <div className="itinerary-modal-body__desc itinerary-tab__content-box">
          <p className="itinerary-tab__content-title">{copy.additionalTour}</p>
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{additionalTour}</Markdown>
        </div>
      )}

      {optionalTour && (
        <div className="itinerary-modal-body__desc itinerary-tab__content-box">
          <p className="itinerary-tab__content-title">{copy.optionalTour}</p>
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{optionalTour}</Markdown>
        </div>
      )}

      {/* Meal code legend */}
      {(hasMOB || hasMGD) && (
        <div className="meal-code-legend">
          <p className="meal-code-legend__title">{copy.mealCodeReference}</p>
          {hasMOB && (
            <p className="meal-code-legend__item"><strong>MOB</strong>{" — "}Meal on Board (机上用餐)</p>
          )}
          {hasMGD && (
            <p className="meal-code-legend__item"><strong>MGD</strong>{" — "}Michelin Guide Dining (米其林指南餐)</p>
          )}
        </div>
      )}

      {/* Special Disclaimer */}
      {t(specialDisclaimer, specialDisclaimerTwo) && (
        <div className="itinerary-tab__disclaimer itinerary-tab__disclaimer--special">
          <p className="itinerary-tab__disclaimer-title">{copy.specialDisclaimer}</p>
          <div className="itinerary-modal-body__desc itinerary-tab__disclaimer--preserve">
            <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {t(specialDisclaimer, specialDisclaimerTwo)}
            </Markdown>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="itinerary-tab__disclaimer">
        <p className="itinerary-tab__disclaimer-title">{copy.disclaimer}</p>
        <ul>
          {copy.disclaimerItems.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default ItineraryPlanV3;
